import React, { useMemo, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { Event } from 'services/api';
import { CheckInTabs } from './CheckInTabs';
import { CHECK_IN_COLORS } from './colors';
import { CheckInResultFlood } from './CheckInResultFlood';
import { EventBlock } from './EventBlock';
import { MyCodeTab } from './MyCodeTab';
import { ScanTab } from './ScanTab';
import { AttendeeTab, ScanResultState, TicketHolder } from './types';

interface AttendeeCheckInProps {
  event: Event;
  receiptId?: string;
  tickets: TicketHolder[];
  onClose: () => void;
}

export const AttendeeCheckIn: React.FC<AttendeeCheckInProps> = ({
  event,
  receiptId,
  tickets,
  onClose,
}) => {
  const ticket = tickets[0];
  const [sessionCheckIn, setSessionCheckIn] = useState<ScanResultState | null>(
    ticket?.checkedInDate
      ? {
          kind: 'checked_in',
          checkedInAt: ticket.checkedInDate,
          ticket: {
            id: '',
            firstName: ticket.firstName,
            lastName: ticket.lastName,
            email: ticket.email,
            ticketNumber: ticket.ticketNumber,
            ticketType:
              ticket.ticketType ||
              event.ticketTypes?.find(t => t.id === ticket.typeId)?.label,
          },
        }
      : null,
  );
  const [tab, setTab] = useState<AttendeeTab>('myCode');

  const tabs = useMemo(
    () => [
      { id: 'myCode', label: 'My Code' },
      { id: 'scan', label: 'Scan' },
    ],
    [],
  );

  if (
    sessionCheckIn?.kind === 'checked_in' ||
    sessionCheckIn?.kind === 'already_checked_in'
  ) {
    const identity = sessionCheckIn.ticket;
    return (
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <CheckInResultFlood
          kind={sessionCheckIn.kind}
          audience="attendee"
          firstName={identity?.firstName || ticket?.firstName}
          lastName={identity?.lastName || ticket?.lastName}
          email={identity?.email || ticket?.email}
          ticketNumber={identity?.ticketNumber || ticket?.ticketNumber}
          ticketType={
            identity?.ticketType ||
            ticket?.ticketType ||
            event.ticketTypes?.find(t => t.id === ticket?.typeId)?.label
          }
          checkedInAt={sessionCheckIn.checkedInAt}
          opensAt={sessionCheckIn.opensAt}
          onClose={onClose}
        />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <CheckInTabs
        tabs={tabs}
        activeId={tab}
        onChange={id => setTab(id as AttendeeTab)}
      />
      {tab === 'myCode' && ticket && receiptId ? (
        <MyCodeTab event={event} receiptId={receiptId} ticket={ticket} />
      ) : tab === 'myCode' ? (
        <Box
          sx={{
            flex: 1,
            minHeight: 0,
            backgroundColor: CHECK_IN_COLORS.sheetBg,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '16px',
            pt: '28px',
            px: '24px',
            textAlign: 'center',
          }}
        >
          <EventBlock event={event} />
          <Typography
            sx={{
              fontFamily: 'Poppins, sans-serif',
              fontWeight: 400,
              fontSize: '14px',
              lineHeight: '22px',
              color: CHECK_IN_COLORS.text,
            }}
          >
            RSVP to this event to get your check-in code, or use Scan to check
            in with the event QR code.
          </Typography>
        </Box>
      ) : (
        <ScanTab
          eventId={event.id}
          method="self_scan"
          onClose={onClose}
          onShowMyCode={() => setTab('myCode')}
          onResult={setSessionCheckIn}
        />
      )}
    </Box>
  );
};
